/**
 * InboxSheet - Unified inbox for messages and notifications
 * Messages tab: Partner nudges
 * Notifications tab: Calendar reminders and gentle reminders
 */

import { useState, useCallback, memo, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  X,
  MessageCircle,
  Bell,
  Heart,
  Calendar,
  Trophy,
  Users,
  TrendingUp,
} from '@/lib/icons';
import { triggerHaptic } from '@/lib/haptics';
import { getPortalRoot, useReducedMotion } from '@/lib/hooks';
import { usePartnerStore } from '@/state/partner';
import { useAuthStore } from '@/state/auth';
import { useNotificationsStore } from '@/state/notifications';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import type { NudgeDocument, NotificationDocument, NotificationType } from '@/lib/types';

type InboxTab = 'messages' | 'notifications';

interface InboxSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

// =============== Message Item ===============

const MessageItem = memo(function MessageItem({
  nudge,
  isFromMe,
  partnerName,
  onMarkViewed,
}: {
  nudge: NudgeDocument;
  isFromMe: boolean;
  partnerName: string;
  onMarkViewed: (id: string) => void;
}) {
  const isUnread = nudge.status === 'sent' && !isFromMe;
  const displayText = nudge.customText || nudge.templateText;
  const timeAgo = getTimeAgo(nudge.createdAt);

  const handleTap = useCallback(() => {
    if (isUnread) {
      triggerHaptic();
      onMarkViewed(nudge.id);
    }
  }, [isUnread, nudge.id, onMarkViewed]);

  return (
    <button
      onClick={handleTap}
      className={`w-full text-left p-4 rounded-card motion-safe:transition-all duration-200
                  ${isUnread
                    ? 'bg-primary-50 border border-primary-100'
                    : 'bg-gray-50/80 border border-transparent'}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-button flex items-center justify-center flex-shrink-0
                        ${isFromMe ? 'bg-gray-100' : 'bg-rose-100'}`}>
          <Heart className={`w-5 h-5 ${isFromMe ? 'text-gray-500' : 'text-rose-500'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="text-body-sm font-medium text-gray-900">
              {isFromMe ? `You to ${partnerName}` : partnerName}
            </span>
            <span className="text-label text-gray-400 flex-shrink-0">{timeAgo}</span>
          </div>
          <p className="text-body-sm text-gray-600 leading-relaxed line-clamp-2">
            {displayText}
          </p>
          {isUnread && (
            <div className="flex items-center gap-1 mt-2">
              <div className="w-2 h-2 rounded-full bg-primary-500" />
              <span className="text-label text-primary-600 font-medium">New</span>
            </div>
          )}
        </div>
      </div>
    </button>
  );
});

// =============== Notification Item ===============

// Get icon and colors for notification type
function getNotificationStyle(type: NotificationType): {
  Icon: React.ElementType;
  bgColor: string;
  iconColor: string;
} {
  switch (type) {
    case 'check_in_reminder':
      return { Icon: Calendar, bgColor: 'bg-primary-100', iconColor: 'text-primary-600' };
    case 'partner_nudge':
      return { Icon: Heart, bgColor: 'bg-rose-100', iconColor: 'text-rose-500' };
    case 'streak_milestone':
      return { Icon: Trophy, bgColor: 'bg-amber-100', iconColor: 'text-amber-600' };
    case 'partner_joined':
      return { Icon: Users, bgColor: 'bg-green-100', iconColor: 'text-green-600' };
    case 'weekly_summary':
      return { Icon: TrendingUp, bgColor: 'bg-blue-100', iconColor: 'text-blue-600' };
    default:
      return { Icon: Bell, bgColor: 'bg-gray-100', iconColor: 'text-gray-500' };
  }
}

const NotificationItem = memo(function NotificationItem({
  notification,
  onMarkRead,
  onNavigate,
}: {
  notification: NotificationDocument;
  onMarkRead: (id: string) => void;
  onNavigate: (deepLink: string) => void;
}) {
  const { Icon, bgColor, iconColor } = getNotificationStyle(notification.type);
  const isUnread = !notification.read;
  const timeAgo = getTimeAgoFromString(notification.createdAt);

  const handleTap = useCallback(() => {
    triggerHaptic();
    if (isUnread) {
      onMarkRead(notification.id);
    }
    if (notification.deepLink) {
      onNavigate(notification.deepLink);
    }
  }, [isUnread, notification.id, notification.deepLink, onMarkRead, onNavigate]);

  return (
    <button
      onClick={handleTap}
      className={`w-full text-left p-4 rounded-card motion-safe:transition-all duration-200
                  ${isUnread
                    ? 'bg-primary-50 border border-primary-100'
                    : 'bg-gray-50/80 border border-transparent'}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-button flex items-center justify-center flex-shrink-0 ${bgColor}`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="text-body-sm font-medium text-gray-900">
              {notification.title}
            </span>
            <span className="text-label text-gray-400 flex-shrink-0">{timeAgo}</span>
          </div>
          <p className="text-body-sm text-gray-600 leading-relaxed line-clamp-2">
            {notification.body}
          </p>
          {isUnread && (
            <div className="flex items-center gap-1 mt-2">
              <div className="w-2 h-2 rounded-full bg-primary-500" />
              <span className="text-label text-primary-600 font-medium">New</span>
            </div>
          )}
        </div>
      </div>
    </button>
  );
});

// =============== Empty State ===============

const EmptyState = memo(function EmptyState({ tab }: { tab: InboxTab }) {
  const Icon = tab === 'messages' ? MessageCircle : Bell;
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-14 h-14 rounded-card bg-gray-100 flex items-center justify-center mb-4">
        <Icon className="w-7 h-7 text-gray-400" />
      </div>
      <h4 className="text-body-lg font-semibold text-gray-900 mb-1">
        {tab === 'messages' ? 'No messages yet' : 'All caught up'}
      </h4>
      <p className="text-body-sm text-gray-500 max-w-[240px]">
        {tab === 'messages'
          ? 'Messages from your partner will appear here'
          : 'No pending notifications'}
      </p>
    </div>
  );
});

// =============== Helper Functions ===============

function getTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function getTimeAgoFromString(isoString: string): string {
  return getTimeAgo(new Date(isoString));
}

// =============== Main Component ===============

function InboxSheetInner({ isOpen, onClose }: InboxSheetProps) {
  const [activeTab, setActiveTab] = useState<InboxTab>('messages');
  const { recentNudges = [], partner, markNudgeViewed } = usePartnerStore();
  const { user } = useAuthStore();
  const {
    notifications,
    loading: notificationsLoading,
    hasMore: hasMoreNotifications,
    fetchNotifications,
    markAsRead,
    subscribeToNotifications,
  } = useNotificationsStore();
  const navigate = useNavigate();
  const sheetRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const prefersReducedMotion = useReducedMotion();

  const partnerName = partner?.displayName || 'Partner';
  const myUid = user?.uid;

  // Subscribe to notifications when sheet opens
  useEffect(() => {
    if (isOpen && myUid) {
      const unsubscribe = subscribeToNotifications(myUid);
      return unsubscribe;
    }
  }, [isOpen, myUid, subscribeToNotifications]);

  // Sort nudges by date, newest first (with fallback for empty/undefined)
  const sortedNudges = useMemo(() => {
    if (!recentNudges || !Array.isArray(recentNudges)) return [];
    return [...recentNudges].sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [recentNudges]);

  const handleMarkViewed = useCallback((nudgeId: string) => {
    markNudgeViewed(nudgeId);
  }, [markNudgeViewed]);

  const handleMarkNotificationRead = useCallback((notificationId: string) => {
    markAsRead(notificationId);
  }, [markAsRead]);

  const handleNotificationNavigate = useCallback((deepLink: string) => {
    onClose();
    // Navigate after a short delay to let the sheet close
    setTimeout(() => {
      navigate(deepLink);
    }, 150);
  }, [onClose, navigate]);

  const handleLoadMoreNotifications = useCallback(() => {
    fetchNotifications(true);
  }, [fetchNotifications]);

  const handleClose = useCallback(() => {
    triggerHaptic();
    onClose();
  }, [onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // ESC key handler
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        triggerHaptic();
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Focus management and focus trap
  useEffect(() => {
    if (!isOpen || !sheetRef.current) return;

    previousActiveElement.current = document.activeElement as HTMLElement;

    const getFocusableElements = () => {
      if (!sheetRef.current) return [];
      return Array.from(
        sheetRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null);
    };

    const focusableElements = getFocusableElements();
    const focusTimer = setTimeout(() => focusableElements[0]?.focus(), 100);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const elements = getFocusableElements();
      if (elements.length === 0) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
      previousActiveElement.current?.focus?.();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const backdropAnimation = prefersReducedMotion ? '' : 'motion-safe:animate-fade-in';
  const sheetAnimation = prefersReducedMotion ? '' : 'motion-safe:animate-slide-up-sheet';

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      {/* Backdrop - touch-action:none prevents iOS scroll-through */}
      <div
        className={`absolute inset-0 bg-black/40 backdrop-blur-sm ${backdropAnimation}`}
        style={{ touchAction: 'none' }}
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        className={`relative w-full max-w-lg bg-white rounded-t-[28px]
                   shadow-[0_-2px_20px_rgba(0,0,0,0.08),0_-8px_32px_rgba(0,0,0,0.06)]
                   ${sheetAnimation}`}
        style={{ maxHeight: '80vh' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="inbox-sheet-title"
        tabIndex={-1}
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-9 h-[5px] bg-gray-200/80 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h3 id="inbox-sheet-title" className="text-body-lg font-semibold text-gray-900">Inbox</h3>
          <button
            onClick={handleClose}
            className="p-2 -mr-2 rounded-full hover:bg-gray-100
                      motion-safe:transition-colors touch-manipulation active:scale-95"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Tab Switcher */}
        <div className="px-5 py-3 flex justify-center">
          <SegmentedControl
            options={[
              { value: 'messages' as InboxTab, label: 'Messages' },
              { value: 'notifications' as InboxTab, label: 'Notifications' },
            ]}
            value={activeTab}
            onChange={setActiveTab}
          />
        </div>

        {/* Content - no bottom padding here; safe-area spacer below handles iOS home indicator */}
        <div
          className="overflow-y-auto overscroll-contain momentum-scroll px-5 pb-3"
          style={{ maxHeight: 'calc(80vh - 160px)' }}
        >
          {activeTab === 'messages' ? (
            sortedNudges.length > 0 ? (
              <div className="space-y-3 pb-6">
                {sortedNudges.map((nudge) => (
                  <MessageItem
                    key={nudge.id}
                    nudge={nudge}
                    isFromMe={nudge.fromUid === myUid}
                    partnerName={partnerName}
                    onMarkViewed={handleMarkViewed}
                  />
                ))}
              </div>
            ) : (
              <EmptyState tab="messages" />
            )
          ) : (
            // Notifications tab
            notifications.length > 0 ? (
              <div className="space-y-3 pb-6">
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkRead={handleMarkNotificationRead}
                    onNavigate={handleNotificationNavigate}
                  />
                ))}
                {hasMoreNotifications && (
                  <button
                    onClick={handleLoadMoreNotifications}
                    disabled={notificationsLoading}
                    className="w-full py-3 text-center text-body-sm text-primary-600 font-medium
                               hover:bg-primary-50 rounded-lg motion-safe:transition-colors
                               disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {notificationsLoading ? 'Loading...' : 'Load more'}
                  </button>
                )}
              </div>
            ) : notificationsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
              </div>
            ) : (
              <EmptyState tab="notifications" />
            )
          )}
        </div>

        {/* Safe area spacer for iOS */}
        <div className="h-[env(safe-area-inset-bottom)]" />
      </div>
    </div>,
    getPortalRoot()
  );
}

export const InboxSheet = memo(InboxSheetInner);

